'use client';

import React from 'react';
import { type PropsWithChildren, type RefAttributes, createContext, useContext } from 'react';
import {
    Tag as AriaTag,
    TagGroup as AriaTagGroup,
    type TagGroupProps as AriaTagGroupProps,
    TagList as AriaTagList,
} from 'react-aria-components';
import { cx } from '@milanote/roma/utils/cx';
import { TagBase, type TagBaseProps } from './tag-base';

export { TagBase } from './tag-base';
export type { TagBaseProps } from './tag-base';

const SEMANTIC_NAME_GROUP = 'RomaTagGroup';
const SEMANTIC_NAME = 'RomaTag';

type TagItemBaseProps = Pick<TagBaseProps, 'leading' | 'variant' | 'color' | 'count' | 'isDisabled'>;

export type TagItem = TagItemBaseProps & {
    id: string;
    label: string;
    className?: string | ((state: { isSelected: boolean; isDisabled: boolean }) => string);
    tagBaseClassName?: string;
    onClose?: (id: string) => void;
};

const TagGroupContext = createContext<{
    size: 'xs' | 'sm' | 'md' | 'lg';
}>({
    size: 'sm',
});

export interface TagGroupProps extends AriaTagGroupProps, RefAttributes<HTMLDivElement> {
    label: string;
    size?: 'xs' | 'sm' | 'md' | 'lg';
}

export const TagGroup = ({ label, size = 'sm', selectionMode = 'none', children, ...otherProps }: TagGroupProps) => {
    return (
        <TagGroupContext.Provider value={{ size }}>
            <AriaTagGroup
                aria-label={label}
                selectionMode={selectionMode}
                {...otherProps}
                className={cx(SEMANTIC_NAME_GROUP, otherProps.className as string)}
            >
                {children}
            </AriaTagGroup>
        </TagGroupContext.Provider>
    );
};

TagGroup.displayName = SEMANTIC_NAME_GROUP;

export const TagList = AriaTagList;

export const Tag = ({
    id,
    leading,
    variant = 'normal',
    color,
    isDisabled,
    count,
    className,
    children,
    onClose,
    tagBaseClassName,
}: PropsWithChildren<TagItem>) => {
    const context = useContext(TagGroupContext);
    const size = context.size;

    return (
        <AriaTag
            id={id}
            isDisabled={isDisabled}
            textValue={typeof children === 'string' ? children : undefined}
            className={(state) =>
                cx(
                    'focus:outline-hidden focus-visible:outline-2 focus-visible:-outline-offset-1 focus-visible:outline-focus-ring',
                    typeof className === 'function' ? className(state) : className,
                )
            }
        >
            {({ allowsRemoving, isSelected }) => (
                <TagBase
                    size={size}
                    leading={leading}
                    variant={variant}
                    color={color}
                    count={count}
                    isDisabled={isDisabled}
                    isSelected={isSelected}
                    allowsRemoving={allowsRemoving}
                    onClose={id && onClose ? () => onClose(id.toString()) : undefined}
                    className={tagBaseClassName}
                >
                    <span className="truncate">{children}</span>
                </TagBase>
            )}
        </AriaTag>
    );
};

Tag.displayName = SEMANTIC_NAME;
